import React, { useEffect, useState } from 'react';

import { getPlaybooks, Playbook } from '../api/ops-console';
import {
  Drawer,
  Empty,
  Field,
  Note,
  OpsPage,
  Pill,
  Row,
  Steps,
  Table,
} from '../../../design-system/ops';

/**
 * The playbook registry — ADM-15-FR-04.
 *
 * Read-only by design, and the subtitle says why: if a situation is not
 * written here, the supported move is to escalate rather than to improvise.
 * There is no button on this screen that does anything to a case, because a
 * playbook has no actions — releasing escrow from one means going to the
 * escrow route and passing its authorization, exactly as if the operator had
 * never opened this. That is ADM-15-FR-05, and the guarantee is the absence.
 */
const AdminPlaybooks: React.FC = () => {
  const [playbooks, setPlaybooks] = useState<Playbook[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Playbook | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPlaybooks();
        if (!cancelled) setPlaybooks(res.playbooks);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudieron cargar los playbooks');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <OpsPage title="Playbooks">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!playbooks) {
    return (
      <OpsPage title="Playbooks">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  return (
    <OpsPage
      title="Playbooks"
      subtitle="Qué hacer, paso a paso. Si no está escrito aquí, escala en lugar de improvisar."
    >
      {playbooks.length === 0 ? (
        <Empty icon="📖" title="Sin playbooks" />
      ) : (
        <Table columns="1fr 150px 90px" head={['Procedimiento', 'Área', 'Versión']}>
          {playbooks.map((p) => (
            <Row key={p.id} onOpen={() => setOpen(p)} label={`Abrir ${p.title}`}>
              <span>
                <b>{p.title}</b>
                <br />
                <span className="mut">{p.steps.length} pasos</span>
              </span>
              <span>
                <Pill>{p.category}</Pill>
              </span>
              {/* The version, not just the name: a case resolved last month was
                  resolved under last month's steps. */}
              <span className="mut">v{p.version}</span>
            </Row>
          ))}
        </Table>
      )}

      <Drawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open?.title ?? ''}
        meta={open ? `Playbook · ${open.category} · v${open.version}` : undefined}
      >
        {open ? (
          <>
            <Field label="Pasos">
              <Steps steps={open.steps.map((s) => s.instruction)} />
            </Field>
            <Field label="Regla que no cambia">
              <Note tone="warn">{open.standingRule}</Note>
            </Field>
          </>
        ) : null}
      </Drawer>
    </OpsPage>
  );
};

export default AdminPlaybooks;
